import CancelIcon from "@mui/icons-material/Clear";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import React from "react";
import { useSelector } from "react-redux";
import { selectTimeOffRequest } from "../slices/timeOffRequest/timeOffRequestSlice.js";
import { ALL_TIME_OFF_STATUSES, ALL_TIME_OFF_TYPES } from "../util/constants";
import { LAST_UPDATED, SHIFT, STATUS, TIME_OFF_TYPE } from "../util/constants.js";
import { getScrubbedTimeOffRequestSnapshot } from "../util/timeOffRequestScrubber.js";
import SlideUpTransition from "./fundamentals/SlideUpTransition";
import TimeOffRequestDialog from "./TimeOffRequestDialog.jsx";

const useStyles = makeStyles((theme) => ({
  filterButton: {
    marginTop: "10px",
    width: "88%",
  },
  title: {
    fontSize: "24px",
    textAlign: "left",
    width: "210px",
    margin: "auto",
    marginTop: "10px",
    marginLeft: "20px",
    whiteSpace: "normal",
  },
  searchInput: {
    width: "47vw",
    left: "5px",
  },
  sortInput: {
    width: "47vw",
    right: "5px",
  },
}));

const TimeOffFilterFormMobile = (props) => {
  const classes = useStyles();
  const employeeTimeOffRequestData = useSelector(selectTimeOffRequest);

  const renderTORTypeFilterMenuItems = () => {
    return ALL_TIME_OFF_TYPES.slice()
      .sort()
      .map((timeOffType) => {
        return (
          <MenuItem key={timeOffType} value={timeOffType}>
            <Checkbox
              color="primary"
              checked={employeeTimeOffRequestData.timeOffTypeFilters.indexOf(timeOffType) > -1}
            />
            <ListItemText primary={timeOffType} />
          </MenuItem>
        );
      });
  };

  const renderStatusFilterMenuItems = () => {
    return ALL_TIME_OFF_STATUSES.slice()
      .sort()
      .map((status) => {
        return (
          <MenuItem key={status} value={status}>
            <Checkbox color="primary" checked={employeeTimeOffRequestData.timeOffStatusFilters.indexOf(status) > -1} />
            <ListItemText primary={status} />
          </MenuItem>
        );
      });
  };

  const renderSortByMenuItems = () => {
    const dateTypes = [SHIFT, STATUS, LAST_UPDATED, TIME_OFF_TYPE];
    return dateTypes
      .slice()
      .sort()
      .map((dateType) => {
        return (
          <MenuItem key={dateType} value={dateType}>
            {dateType}
          </MenuItem>
        );
      });
  };

  const SearchButton = () => {
    return (
      <IconButton
        size="small"
        className="search-icon-button"
        onMouseDown={props.onSearchSubmit.bind(this, employeeTimeOffRequestData.searchString)}
      >
        <SearchIcon />
      </IconButton>
    );
  };

  const CancelButton = () => {
    return (
      <IconButton size="small" className="cancel-icon-button" onMouseDown={props.onSearchCancel.bind(this)}>
        <CancelIcon />
      </IconButton>
    );
  };

  const renderTradeFilterDialog = () => {
    return (
      <Dialog
        fullScreen
        className="detail-dialog"
        fullWidth
        maxWidth="md"
        open={props.openMobileFilterDialog}
        TransitionComponent={SlideUpTransition}
        keepMounted
        onClose={() => {
          props.onMobileFilterDialogClose();
        }}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
        data-testid="trade-filter-form-mobile"
      >
        <DialogTitle id="alert-dialog-slide-title" className="detail-dialog-title">
          Filters
        </DialogTitle>
        <DialogContent className="detail-dialog-content">
          <DialogContentText component={"div"} id="alert-dialog-slide-description">
            <Grid container>
              <Grid item xs={12}>
                <Box mt={1} mb={1}>
                  {employeeTimeOffRequestData.timeOffTypeFilters.length > 0 ? (
                    <FormControl variant="outlined">
                      <InputLabel>Time Off Type</InputLabel>
                      <Select
                        id="timeOffTypeFilterElement"
                        className="filter-input"
                        label="Time Off Type"
                        multiple={true}
                        MenuProps={{ autoFocus: false }}
                        value={employeeTimeOffRequestData.timeOffTypeFilters}
                        onChange={props.onTimeOffTypeFilterChange}
                        renderValue={(selected) =>
                          selected.length === ALL_TIME_OFF_TYPES.length
                            ? "ALL"
                            : selected.length === 1
                            ? selected[0]
                            : "MULTI"
                        }
                      >
                        <MenuItem disabled>
                          <em>Time Off Type</em>
                        </MenuItem>
                        <MenuItem key="select-all" value="Select All">
                          <Checkbox
                            color="primary"
                            checked={props.isTimeOffTypeSelectAllChecked()}
                            indeterminate={props.isTimeOffTypeSelectAllIndeterminate()}
                            indeterminateIcon={<IndeterminateCheckBoxIcon color="primary" />}
                          />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {renderTORTypeFilterMenuItems()}
                      </Select>
                    </FormControl>
                  ) : (
                    <FormControl variant="outlined">
                      <InputLabel>Time Off Type</InputLabel>
                      <Select
                        error
                        id="timeOffTypeFilterElement"
                        className="filter-input"
                        label="Time Off Type"
                        multiple={true}
                        MenuProps={{ autoFocus: false }}
                        value={employeeTimeOffRequestData.timeOffTypeFilters}
                        onChange={props.onTimeOffTypeFilterChange}
                        renderValue={(selected) =>
                          selected.length === ALL_TIME_OFF_TYPES.length
                            ? "ALL"
                            : selected.length === 1
                            ? selected[0]
                            : "MULTI"
                        }
                      >
                        <MenuItem disabled>
                          <em>Filter on Time Off Type</em>
                        </MenuItem>
                        <MenuItem key="select-all" value="Select All">
                          <Checkbox
                            color="primary"
                            checked={props.isTimeOffTypeSelectAllChecked()}
                            indeterminate={props.isTimeOffTypeSelectAllIndeterminate()}
                            indeterminateIcon={<IndeterminateCheckBoxIcon color="primary" />}
                          />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {renderTORTypeFilterMenuItems()}
                      </Select>
                      <FormHelperText error>At least one Time Off Type must be selected</FormHelperText>
                    </FormControl>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box mt={1} mb={1}>
                  {employeeTimeOffRequestData.timeOffStatusFilters.length > 0 ? (
                    <FormControl variant="outlined">
                      <InputLabel>Status</InputLabel>
                      <Select
                        id="timeOffStatusSelectElement"
                        className="filter-input"
                        label="Status"
                        multiple={true}
                        MenuProps={{ autoFocus: false }}
                        value={employeeTimeOffRequestData.timeOffStatusFilters}
                        onChange={props.onStatusFilterChange}
                        renderValue={(selected) =>
                          selected.length === ALL_TIME_OFF_STATUSES.length
                            ? "ALL"
                            : selected.length === 1
                            ? selected[0]
                            : "MULTI"
                        }
                      >
                        <MenuItem disabled>
                          <em>Filter on Status</em>
                        </MenuItem>
                        <MenuItem key="select-all" value="Select All">
                          <Checkbox
                            color="primary"
                            checked={props.isStatusSelectAllChecked()}
                            indeterminate={props.isStatusSelectAllIndeterminate()}
                            indeterminateIcon={<IndeterminateCheckBoxIcon color="primary" />}
                          />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {renderStatusFilterMenuItems()}
                      </Select>
                    </FormControl>
                  ) : (
                    <FormControl variant="outlined">
                      <InputLabel>Status</InputLabel>
                      <Select
                        error
                        id="timeOffStatusSelectElement"
                        className="filter-input"
                        label="Status"
                        multiple={true}
                        MenuProps={{ autoFocus: false }}
                        value={employeeTimeOffRequestData.timeOffStatusFilters}
                        onChange={props.onStatusFilterChange}
                        renderValue={(selected) =>
                          selected.length === ALL_TIME_OFF_STATUSES.length
                            ? "ALL"
                            : selected.length === 1
                            ? selected[0]
                            : "MULTI"
                        }
                      >
                        <MenuItem disabled>
                          <em>Filter on Status</em>
                        </MenuItem>
                        <MenuItem key="select-all" value="Select All">
                          <Checkbox
                            color="primary"
                            checked={props.isStatusSelectAllChecked()}
                            indeterminate={props.isStatusSelectAllIndeterminate()}
                            indeterminateIcon={<IndeterminateCheckBoxIcon color="primary" />}
                          />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {renderStatusFilterMenuItems()}
                      </Select>
                      <FormHelperText error>At least one Status must be selected</FormHelperText>
                    </FormControl>
                  )}
                </Box>
              </Grid>
            </Grid>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            className="dialog-left-button"
            variant="outlined"
            color="primary"
            onClick={() => props.onMobileFilterDialogClose()}
          >
            Cancel
          </Button>
          <Button
            className="dialog-right-button-2"
            variant="contained"
            color="primary"
            onClick={() => props.onClickMobileFilterDialogApplyFiltersButton()}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <div className="employee-time-off-filter-form">
      <>
        <Grid container justifyContent="center" spacing={1}>
          <Grid container className="mobile-title-row">
            <Grid item xs={6}>
              <Typography component={"div"}>
                <Box className={classes.title}>{props?.title}</Box>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Button
                className={classes.filterButton}
                variant="contained"
                color="primary"
                onClick={() => {
                  props.onClickMobileFilterDialogOpen();
                }}
              >
                Filters
              </Button>
            </Grid>
          </Grid>
          <Grid item xs={6}>
            <Box mt={1} mb={1}>
              <TextField
                error={employeeTimeOffRequestData?.searchStringErrors !== ""}
                className={classes.searchInput}
                label="Search"
                variant="outlined"
                value={employeeTimeOffRequestData?.searchString}
                onChange={props?.onSearchChange.bind(this)}
                onBlur={props?.onSearchBlur.bind(this)}
                onKeyUp={props?.onSearchEnterKeyPressed.bind(this)}
                helperText={employeeTimeOffRequestData?.searchStringErrors}
                InputProps={{
                  endAdornment:
                    employeeTimeOffRequestData?.searchString !== "" ? (
                      <InputAdornment position="end">
                        {employeeTimeOffRequestData?.timeOffRequestFoundByReferenceNumberSearchIsLoading ? (
                          <CircularProgress size={16} />
                        ) : (
                          ""
                        )}
                        <CancelButton />
                        <SearchButton />
                      </InputAdornment>
                    ) : (
                      <InputAdornment position="end">
                        <SearchButton />
                      </InputAdornment>
                    ),
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box mt={1} mb={1}>
              <FormControl variant="outlined">
                <InputLabel>Sort By</InputLabel>
                <Select
                  id="employeeTimeOffFormTypeSelectElement"
                  className={classes.sortInput}
                  defaultValue={SHIFT}
                  value={employeeTimeOffRequestData.mobileSortByValue}
                  onChange={props.onMobileSortByChange}
                  label="Sort By"
                >
                  <MenuItem disabled>
                    <em>Sort By</em>
                  </MenuItem>
                  {renderSortByMenuItems()}
                </Select>
              </FormControl>
            </Box>
          </Grid>
          {props.openMobileFilterDialog && renderTradeFilterDialog()}
          {employeeTimeOffRequestData.timeOffRequestFoundByReferenceNumberSearchIsDialogOpen &&
          !employeeTimeOffRequestData.timeOffRequestFoundByReferenceNumberSearchIsLoading ? (
            <TimeOffRequestDialog
              timeOffRequest={
                employeeTimeOffRequestData.timeOffRequestFoundByReferenceNumberSearch
                  ? getScrubbedTimeOffRequestSnapshot(
                      employeeTimeOffRequestData.timeOffRequestFoundByReferenceNumberSearch
                    )
                  : employeeTimeOffRequestData.timeOffRequestFoundByReferenceNumberSearch
              }
              isDialogOpen={employeeTimeOffRequestData.timeOffRequestFoundByReferenceNumberSearchIsDialogOpen}
              handleClickDialogOpen={() => props.onClickTimeOffRequestFoundByReferenceNumberSearchDialogOpen()}
              handleClickDialogClose={() => props.onClickTimeOffRequestFoundByReferenceNumberSearchDialogClose()}
              isAdminPage={props.isAdminPage}
              isDesktopView={props.isDesktopView}
            />
          ) : (
            ""
          )}
        </Grid>
      </>
    </div>
  );
};

TimeOffFilterFormMobile.propTypes = {
  title: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func,
  mobileSortByValue: PropTypes.string.isRequired,
  onMobileSortByChange: PropTypes.func.isRequired,
};

export default TimeOffFilterFormMobile;
